import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { logger } from '../utils/logger';

export interface CookiePreferences {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsentData {
  timestamp: string;
  preferences: CookiePreferences;
  version: string; // Para manejar cambios en la política
}

const COOKIE_CONSENT_KEY = 'guideme-cookie-consent';
const CURRENT_POLICY_VERSION = '1.0';
const DISABLE_DB_SYNC = true; // TEMPORARILY DISABLED - Set to false when DB issues are resolved

const loadScript = (id: string, src: string, attrs: Record<string, string> = {}) => {
  if (typeof document === 'undefined' || document.getElementById(id)) return;
  const el = document.createElement('script');
  el.id = id;
  el.async = true;
  el.src = src;
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  document.head.appendChild(el);
};

const loadAnalyticsScripts = () => {
  loadScript('ga4', 'https://www.googletagmanager.com/gtag/js?id=G-T6592M4VY8');
  loadScript('seline', 'https://cdn.seline.com/seline.js', { 'data-token': 'ec3654feec35791' });
};

export const useCookieConsent = () => {
  const { user, token } = useAuth();
  const [hasConsent, setHasConsent] = useState<boolean>(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false
  });
  const [isLoading, setIsLoading] = useState(true);

  // Cargar preferencias al inicializar
  useEffect(() => {
    loadCookiePreferences();
  }, [user?.id]); // Solo cuando cambie el ID del usuario

  const loadCookiePreferences = async () => {
    setIsLoading(true);
    
    try {
      // 1. Primero intentar cargar desde la base de datos (si hay usuario logueado y no está desactivado)
      if (user && token && !DISABLE_DB_SYNC) {
        const dbPreferences = await loadFromDatabase();
        if (dbPreferences) {
          setPreferences(dbPreferences.preferences);
          setHasConsent(true);
          // Sincronizar con localStorage
          saveToLocalStorage(dbPreferences);
          setIsLoading(false);
          return;
        }
      }

      // 2. Si no hay preferencias en DB, cargar desde localStorage
      const localPreferences = loadFromLocalStorage();
      if (localPreferences) {
        setPreferences(localPreferences.preferences);
        setHasConsent(true);
        applyCookiePreferences(localPreferences.preferences);
        
        // Si hay usuario logueado, sincronizar con DB (si no está desactivado)
        if (user && token && !DISABLE_DB_SYNC) {
          await saveToDatabase(localPreferences);
        }
      }
    } catch (error) {
      logger.error('Error loading cookie preferences:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadFromLocalStorage = (): CookieConsentData | null => {
    try {
      const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (stored) {
        const data: CookieConsentData = JSON.parse(stored);
        // Verificar si la versión de la política es actual
        if (data.version === CURRENT_POLICY_VERSION) {
          return data;
        }
      }
    } catch (error) {
      logger.error('Error loading from localStorage:', error);
    }
    return null;
  };

  const saveToLocalStorage = (data: CookieConsentData) => {
    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(data));
    } catch (error) {
      logger.error('Error saving to localStorage:', error);
    }
  };

  const loadFromDatabase = async (): Promise<CookieConsentData | null> => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/user/cookie-preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        return data.preferences;
      } else {
        console.warn('Failed to load cookie preferences from database:', response.status);
        return null;
      }
    } catch (error) {
      console.warn('Database not available for cookie preferences, using localStorage only:', error);
      return null;
    }
  };

  const saveToDatabase = async (data: CookieConsentData) => {
    if (!user || !token || DISABLE_DB_SYNC) return;

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/user/cookie-preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.warn('Cookie preferences save failed:', response.status, errorText);
        // No lanzar error - solo guardar localmente
        return;
      }

      logger.info('Cookie preferences saved to database successfully');
    } catch (error) {
      console.warn('Error saving cookie preferences to database (will save locally only):', error);
      // No lanzar error - continúa con localStorage solamente
    }
  };

  const savePreferences = async (newPreferences: CookiePreferences) => {
    const consentData: CookieConsentData = {
      timestamp: new Date().toISOString().slice(0, 19).replace('T', ' '),
      preferences: newPreferences,
      version: CURRENT_POLICY_VERSION
    };

    // Guardar en estado local
    setPreferences(newPreferences);
    setHasConsent(true);

    // Guardar en localStorage (siempre)
    saveToLocalStorage(consentData);

    // Guardar en base de datos (si hay usuario y no está desactivado)
    if (user && token && !DISABLE_DB_SYNC) {
      await saveToDatabase(consentData);
    }

    // Aplicar las preferencias inmediatamente
    applyCookiePreferences(newPreferences);
  };

  const applyCookiePreferences = (prefs: CookiePreferences) => {
    // Funcional: Configurar idioma, tema, etc.
    if (prefs.functional) {
      // Habilitar cookies funcionales
      logger.debug('Functional cookies enabled');
    } else {
      // Deshabilitar cookies funcionales no esenciales
      logger.debug('Functional cookies disabled');
    }

    // Analytics: GA y Seline no se cargan hasta que el usuario acepta (LSSI 22.2).
    if (prefs.analytics) {
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: 'granted'
        });
      }
      loadAnalyticsScripts();
      logger.debug('Analytics cookies enabled');
    } else {
      // Deshabilitar analytics
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: 'denied'
        });
      }
      logger.debug('Analytics cookies disabled');
    }

    // Marketing: Publicidad, redes sociales
    if (prefs.marketing) {
      // Habilitar cookies de marketing
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('consent', 'update', {
          ad_storage: 'granted',
          ad_user_data: 'granted',
          ad_personalization: 'granted'
        });
      }
      logger.debug('Marketing cookies enabled');
    } else {
      // Deshabilitar cookies de marketing
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('consent', 'update', {
          ad_storage: 'denied',
          ad_user_data: 'denied',
          ad_personalization: 'denied'
        });
      }
      logger.debug('Marketing cookies disabled');
    }
  };

  const acceptAll = async () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    };
    await savePreferences(allAccepted);
  };

  const acceptNecessaryOnly = async () => {
    const necessaryOnly: CookiePreferences = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    };
    await savePreferences(necessaryOnly);
  };

  const resetConsent = () => {
    // Limpiar localStorage
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    
    // Resetear estado
    setHasConsent(false);
    setPreferences({
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    });

    // Si hay usuario, también limpiar de DB
    if (user && token) {
      fetch(`${import.meta.env.VITE_API_URL}/api/user/cookie-preferences`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      }).catch((error) => logger.error('Error deleting cookie preferences from database:', error));
    }
  };

  return {
    hasConsent,
    preferences,
    isLoading,
    savePreferences,
    acceptAll,
    acceptNecessaryOnly,
    resetConsent,
    applyCookiePreferences
  };
};

// Declarar tipos para gtag (Google Analytics)
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}